import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown } from 'lucide-react';

interface FAQItem {
  question: string;
  answer: React.ReactNode;
}

const faqData: FAQItem[] = [
  {
    question: "O que a Tavares Consulting faz?",
    answer: (
      <p>
        Somos uma consultoria especializada em logística e supply chain, ajudando empresas a reduzir custos, 
        otimizar operações e melhorar a eficiência logística. Atuamos em transporte, armazenagem, distribuição 
        e processos operacionais, além de oferecer suporte em mudanças de Centros de Distribuição (CDs) e 
        re-layout de armazéns.
      </p>
    )
  },
  {
    question: "Quais serviços de logística vocês oferecem?",
    answer: (
      <div className="space-y-4">
        <p>Oferecemos soluções personalizadas para diferentes áreas da logística:</p>
        <ul className="list-none space-y-2">
          <li className="flex items-start">
            <span className="text-blue-600 mr-2">✅</span>
            <span>Gestão de Transporte – Estratégias para redução de custos, negociação com transportadoras e roteirização otimizada.</span>
          </li>
          <li className="flex items-start">
            <span className="text-blue-600 mr-2">✅</span>
            <span>Armazenagem e Estoques – Planejamento de CD's, controle de inventário e otimização de espaço.</span>
          </li>
          <li className="flex items-start">
            <span className="text-blue-600 mr-2">✅</span>
            <span>Distribuição e Last Mile – Modelos eficientes para entregas urbanas e planejamento de redes logísticas.</span>
          </li>
          <li className="flex items-start">
            <span className="text-blue-600 mr-2">✅</span>
            <span>Mudança e Re-layout de CDs – Planejamento para realocação, melhoria de espaço e aumento da produtividade.</span>
          </li>
          <li className="flex items-start">
            <span className="text-blue-600 mr-2">✅</span>
            <span>Consultoria de Processos – Mapeamento, automação e melhoria contínua das operações logísticas.</span>
          </li>
          <li className="flex items-start">
            <span className="text-blue-600 mr-2">✅</span>
            <span>Tecnologia na Logística – Implementação de inteligência artificial e automação para maior eficiência.</span>
          </li>
        </ul>
        <p className="text-blue-600 font-medium">
          📢 Precisa de uma solução específica? Entre em contato para uma análise personalizada!
        </p>
      </div>
    )
  },
  {
    question: "Em quais mercados a consultoria atua?",
    answer: (
      <div className="space-y-4">
        <p>Atendemos empresas de diferentes setores e portes, tanto B2B quanto B2C, incluindo:</p>
        <ul className="list-none space-y-2">
          <li className="flex items-center">
            <span className="mr-2">🐾</span>
            <span>Setor Pet – Logística para distribuidores e lojas do setor pet.</span>
          </li>
          <li className="flex items-center">
            <span className="mr-2">⚽</span>
            <span>Artigos Esportivos – Gestão logística para redes de varejo e indústrias esportivas.</span>
          </li>
          <li className="flex items-center">
            <span className="mr-2">🍏</span>
            <span>Alimentos e Bebidas – Planejamento de estoque e transporte refrigerado.</span>
          </li>
          <li className="flex items-center">
            <span className="mr-2">☀️</span>
            <span>Energia Solar – Distribuição de painéis e equipamentos para energia renovável.</span>
          </li>
          <li className="flex items-center">
            <span className="mr-2">🛍️</span>
            <span>Varejo e E-commerce – Estratégias omnichannel e logística integrada.</span>
          </li>
          <li className="flex items-center">
            <span className="mr-2">💊</span>
            <span>Indústria Farmacêutica – Transporte e armazenagem de medicamentos e insumos.</span>
          </li>
        </ul>
        <p className="text-blue-600 font-medium">
          Se o seu setor não está na lista, fale conosco para entender como podemos ajudar!
        </p>
      </div>
    )
  },
  {
    question: "O que é a consultoria de processos logísticos?",
    answer: (
      <div className="space-y-4">
        <p>
          Nossa consultoria analisa e mapeia todas as operações logísticas da empresa, identificando 
          ineficiências, gargalos e oportunidades de melhoria. O objetivo é garantir maior controle, 
          redução de desperdícios e processos mais ágeis.
        </p>
        <p>Principais etapas do serviço:</p>
        <ul className="list-none space-y-2">
          <li className="flex items-center">
            <span className="text-blue-600 mr-2">🔹</span>
            <span>Diagnóstico inicial e identificação de pontos críticos.</span>
          </li>
          <li className="flex items-center">
            <span className="text-blue-600 mr-2">🔹</span>
            <span>Propostas de melhoria com soluções estratégicas e inovadoras.</span>
          </li>
          <li className="flex items-center">
            <span className="text-blue-600 mr-2">🔹</span>
            <span>Implementação e monitoramento dos resultados.</span>
          </li>
        </ul>
      </div>
    )
  },
  {
    question: "Como a consultoria pode melhorar a eficiência logística?",
    answer: (
      <div className="space-y-4">
        <ul className="list-none space-y-2">
          <li className="flex items-start">
            <span className="text-blue-600 mr-2">✔️</span>
            <span>Identificando gargalos e desperdícios no processo operacional.</span>
          </li>
          <li className="flex items-start">
            <span className="text-blue-600 mr-2">✔️</span>
            <span>Implementando tecnologia e automação para otimizar fluxos logísticos.</span>
          </li>
          <li className="flex items-start">
            <span className="text-blue-600 mr-2">✔️</span>
            <span>Melhorando a roteirização e gestão de transportes para entregas mais rápidas e eficientes.</span>
          </li>
          <li className="flex items-start">
            <span className="text-blue-600 mr-2">✔️</span>
            <span>Reduzindo custos logísticos sem comprometer a qualidade do serviço.</span>
          </li>
          <li className="flex items-start">
            <span className="text-blue-600 mr-2">✔️</span>
            <span>Aprimorando o controle de estoque, evitando excessos e rupturas.</span>
          </li>
        </ul>
        <p className="text-blue-600 font-medium">
          📊 Nossos clientes alcançam melhorias de até 25% na eficiência operacional e reduções de custos superiores a 15%.
        </p>
      </div>
    )
  },
  {
    question: "Qual a importância da consultoria logística para as empresas?",
    answer: (
      <div className="space-y-4">
        <p>Investir em consultoria logística permite que sua empresa:</p>
        <ul className="list-none space-y-2">
          <li className="flex items-start">
            <span className="text-blue-600 mr-2">✅</span>
            <span>Reduza custos operacionais e aumente a lucratividade.</span>
          </li>
          <li className="flex items-start">
            <span className="text-blue-600 mr-2">✅</span>
            <span>Melhore a eficiência das entregas e a satisfação do cliente.</span>
          </li>
          <li className="flex items-start">
            <span className="text-blue-600 mr-2">✅</span>
            <span>Tenha mais controle e previsibilidade sobre processos logísticos.</span>
          </li>
          <li className="flex items-start">
            <span className="text-blue-600 mr-2">✅</span>
            <span>Acompanhe as melhores práticas do mercado e se mantenha competitiva.</span>
          </li>
        </ul>
      </div>
    )
  },
  {
    question: "Como a consultoria de processos beneficia a empresa?",
    answer: (
      <div className="space-y-4">
        <ul className="list-none space-y-2">
          <li className="flex items-center">
            <span className="mr-2">📊</span>
            <span>Aumento da produtividade – Processos mais ágeis e organizados.</span>
          </li>
          <li className="flex items-center">
            <span className="mr-2">🎯</span>
            <span>Melhoria na qualidade dos serviços – Atendimento e operações mais eficientes.</span>
          </li>
          <li className="flex items-center">
            <span className="mr-2">😊</span>
            <span>Satisfação dos funcionários e clientes – Equipes mais produtivas e clientes mais satisfeitos.</span>
          </li>
          <li className="flex items-center">
            <span className="mr-2">💰</span>
            <span>Redução de custos – Melhor aproveitamento de recursos e estrutura logística.</span>
          </li>
        </ul>
      </div>
    )
  },
  {
    question: "Como a Tavares Consulting executa um projeto de consultoria logística?",
    answer: (
      <div className="space-y-4">
        <p>Nosso método envolve 4 etapas principais para garantir soluções práticas e eficazes:</p>
        <ul className="list-none space-y-2">
          <li className="flex items-start">
            <span className="text-blue-600 mr-2">1️⃣</span>
            <span>Diagnóstico e Levantamento de Dados – Analisamos a operação atual e identificamos os principais desafios.</span>
          </li>
          <li className="flex items-start">
            <span className="text-blue-600 mr-2">2️⃣</span>
            <span>Desenvolvimento de Soluções – Criamos um plano estratégico personalizado para otimizar a logística da empresa.</span>
          </li>
          <li className="flex items-start">
            <span className="text-blue-600 mr-2">3️⃣</span>
            <span>Implementação e Treinamento – Aplicamos as melhorias e capacitamos sua equipe para garantir eficiência.</span>
          </li>
          <li className="flex items-start">
            <span className="text-blue-600 mr-2">4️⃣</span>
            <span>Acompanhamento e Monitoramento – Mensuramos os resultados e ajustamos as estratégias conforme necessário.</span>
          </li>
        </ul>
        <p className="text-blue-600 font-medium">
          🔎 Cada projeto é único! Entre em contato para um plano sob medida para seu negócio.
        </p>
      </div>
    )
  },
  {
    question: "Qual a abordagem da consultoria para promover melhorias nos processos?",
    answer: (
      <ul className="list-none space-y-2">
        <li className="flex items-start">
          <span className="text-blue-600 mr-2">✔️</span>
          <span>Análise detalhada das operações logísticas.</span>
        </li>
        <li className="flex items-start">
          <span className="text-blue-600 mr-2">✔️</span>
          <span>Identificação de oportunidades de melhoria para eficiência e redução de custos.</span>
        </li>
        <li className="flex items-start">
          <span className="text-blue-600 mr-2">✔️</span>
          <span>Implementação de soluções práticas e personalizadas, adaptadas ao seu negócio.</span>
        </li>
        <li className="flex items-start">
          <span className="text-blue-600 mr-2">✔️</span>
          <span>Acompanhamento e ajustes contínuos para garantir resultados sustentáveis.</span>
        </li>
      </ul>
    )
  },
  {
    question: "A consultoria ajuda na escolha de transportadoras?",
    answer: (
      <p>
        Sim! Podemos avaliar as transportadoras atuais, sugerir novas opções, e definir contratos mais 
        vantajosos. Também analisamos custos de frete, eficiência operacional e qualidade do serviço prestado.
      </p>
    )
  },
  {
    question: "A consultoria pode ajudar na gestão de estoque?",
    answer: (
      <div className="space-y-4">
        <p>Sim! Implementamos métodos avançados de controle de estoque para:</p>
        <ul className="list-none space-y-2">
          <li className="flex items-start">
            <span className="text-blue-600 mr-2">✔️</span>
            <span>Evitar excessos e rupturas de mercadorias.</span>
          </li>
          <li className="flex items-start">
            <span className="text-blue-600 mr-2">✔️</span>
            <span>Otimizar a distribuição de produtos nos Centros de Distribuição.</span>
          </li>
          <li className="flex items-start">
            <span className="text-blue-600 mr-2">✔️</span>
            <span>Aprimorar o controle de inventário para melhor previsibilidade de demandas.</span>
          </li>
        </ul>
      </div>
    )
  },
  {
    question: "Vocês utilizam tecnologia na consultoria?",
    answer: (
      <div className="space-y-4">
        <p>Sim! Integramos ferramentas tecnológicas e inteligência artificial para:</p>
        <ul className="list-none space-y-2">
          <li className="flex items-start">
            <span className="text-blue-600 mr-2">✅</span>
            <span>Otimizar rotas de entrega e reduzir custos de transporte.</span>
          </li>
          <li className="flex items-start">
            <span className="text-blue-600 mr-2">✅</span>
            <span>Prever demandas e melhorar o planejamento de estoque.</span>
          </li>
          <li className="flex items-start">
            <span className="text-blue-600 mr-2">✅</span>
            <span>Automatizar processos operacionais para aumentar a produtividade.</span>
          </li>
        </ul>
        <p className="text-blue-600 font-medium">
          🔎 Nossa consultoria vai além da análise tradicional e utiliza dados e tecnologia para soluções mais eficazes.
        </p>
      </div>
    )
  },
  {
    question: "Como posso contratar a consultoria?",
    answer: (
      <div className="space-y-4">
        <p>
          📩 Basta entrar em contato pelo nosso formulário ou WhatsApp para uma avaliação inicial gratuita. 
          Nossa equipe analisará suas necessidades e montará um plano estratégico para otimizar sua logística.
        </p>
        <p className="text-blue-600 font-medium">
          📢 Pronto para transformar sua logística? Entre em contato agora e descubra como podemos ajudar!
        </p>
      </div>
    )
  }
];

export default function FAQ() {
  const [openItems, setOpenItems] = useState<number[]>([]);

  const toggleItem = (index: number) => {
    setOpenItems(prev => 
      prev.includes(index) 
        ? prev.filter(i => i !== index)
        : [...prev, index]
    );
  };

  const midPoint = Math.ceil(faqData.length / 2);
  const leftColumnFAQs = faqData.slice(0, midPoint);
  const rightColumnFAQs = faqData.slice(midPoint);

  return (
    <section id="faq" className="py-24 bg-gradient-to-b from-gray-50 to-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-4xl font-bold text-gray-900 mb-4">
            Perguntas Frequentes
          </h2>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Tire suas dúvidas sobre nossa consultoria logística e descubra como podemos ajudar sua empresa
          </p>
          <p className="mt-4 text-base text-blue-600 max-w-2xl mx-auto">
            <Link to="/faq" className="font-medium hover:underline">
              Ver também: perguntas sobre frete, CD, OTIF e TMS →
            </Link>
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Coluna da esquerda */}
          <div className="space-y-6">
            {leftColumnFAQs.map((item, index) => (
              <div
                key={index}
                className="bg-white rounded-xl shadow-sm overflow-hidden transition-all duration-300 hover:shadow-md border border-gray-100"
              >
                <button
                  onClick={() => toggleItem(index)}
                  className="w-full px-8 py-6 text-left flex justify-between items-center hover:bg-blue-50/50 transition-colors group"
                >
                  <span className="font-semibold text-gray-900 flex-1 pr-4 group-hover:text-blue-600">
                    {item.question}
                  </span>
                  <ChevronDown
                    className={`w-6 h-6 text-blue-600 transition-transform duration-300 flex-shrink-0 ${
                      openItems.includes(index) ? 'transform rotate-180' : ''
                    }`}
                  />
                </button>
                {openItems.includes(index) && (
                  <div className="px-8 py-6 bg-gray-50/70">
                    <div className="prose max-w-none text-gray-600 [&>p]:leading-relaxed [&>ul]:space-y-3">
                      {item.answer}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Coluna da direita */}
          <div className="space-y-6">
            {rightColumnFAQs.map((item, index) => (
              <div
                key={index + midPoint}
                className="bg-white rounded-xl shadow-sm overflow-hidden transition-all duration-300 hover:shadow-md border border-gray-100"
              >
                <button
                  onClick={() => toggleItem(index + midPoint)}
                  className="w-full px-8 py-6 text-left flex justify-between items-center hover:bg-blue-50/50 transition-colors group"
                >
                  <span className="font-semibold text-gray-900 flex-1 pr-4 group-hover:text-blue-600">
                    {item.question}
                  </span>
                  <ChevronDown
                    className={`w-6 h-6 text-blue-600 transition-transform duration-300 flex-shrink-0 ${
                      openItems.includes(index + midPoint) ? 'transform rotate-180' : ''
                    }`}
                  />
                </button>
                {openItems.includes(index + midPoint) && (
                  <div className="px-8 py-6 bg-gray-50/70">
                    <div className="prose max-w-none text-gray-600 [&>p]:leading-relaxed [&>ul]:space-y-3">
                      {item.answer}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}